import { COUNT_ACCURACY, DEFAULT_ACCURACY, LESS_ACCURACY, PERCENTAGE_ACCURACY } from "constant/report";
import { NullableNumber, NullableMoment, ReportItemType } from "types/Reports";
import { addDecimalPlace } from "utils/functions";

export const formatCountInReport = (value: NullableNumber) => {
    if (value === null) {
        return '-';
    }

    return addDecimalPlace(value, COUNT_ACCURACY);
};

export const formatValueInReport = (value: NullableNumber) => {
    if (value === null) {
        return '-';
    }

    return addDecimalPlace(value, DEFAULT_ACCURACY);
};

export const formatValueLessAccInReport = (value: NullableNumber) => {
    if (value === null) {
        return '-';
    }

    return addDecimalPlace(value, LESS_ACCURACY);
};

export const formatValuesInReport = (values: NullableNumber[]) => {
    if (values.length === 0) {
        return [];
    }

    return values.map((value) => {
        if (value === null) {
            return null;
        }

        return Number.parseFloat(addDecimalPlace(value, DEFAULT_ACCURACY));
    });
};

export const formatValuePercentInReport = (value: NullableNumber) => {
    if (value === null) {
        return '-';
    }

    return addDecimalPlace(Math.abs(value), PERCENTAGE_ACCURACY);
};

export const formatDateInReport = (date: NullableMoment | undefined, dateFormat: string) => {
    if (date === undefined || date === null) {
        return '--';
    }

    return date.format(dateFormat);
};

export const convertHoursAndMinutesToHours = (input: string) => {
    const splitPart = input.includes('hours') ? 'hours' : 'h';

    const parts = input.split(splitPart);
    const hours = Number.parseInt(parts[0]);
    const minutes = Number.parseInt(parts[1]);

    return hours + (minutes / 60);
};

export const convertHoursToHoursAndMinutes = (inputHours: number) => {
    if (Number.isInteger(inputHours)) {
        return { hours: inputHours, minutes: 0};
    }

    let parts = inputHours.toString().split('.');
    let hoursPart = parts[0];
    let minutesPart = parts[1];

    return {
        hours: Number.parseInt(hoursPart),
        minutes: Math.floor(Number.parseFloat(`0.${minutesPart}`) * 60),
    };
};

export const formatValueHourInReport = (value: NullableNumber, showFullUnit = false, autoHideMinutes = false) => {
    if (value === null) {
        return '- h - m';
    }

    const part = convertHoursToHoursAndMinutes(value);
    let hours = 'h';
    let minutes = 'm';

    if (showFullUnit) {
        hours = 'hours';
        minutes = 'minutes';
    }

    let result = `${part.hours} ${hours}`;

    if (part.minutes === 0 && autoHideMinutes === true) {
        return result;
    }

    result += ` ${part.minutes} ${minutes}`;

    return result;
};

export const formatDataInReportChartSeries = (
    data: any[],
    weekdayIndexes: number[],
    weekendIndexes: number[],
) => {
    const weekdaysData: NullableNumber[] = [];
    const weekendsData: NullableNumber[] = [];
    const allDaysData: NullableNumber[] = [];

    for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const isInvalidNumber = item === undefined || Number.isNaN(item) || typeof item !== 'number';

        if (isInvalidNumber) {
            allDaysData.push(null);
        } else {
            allDaysData.push(item);
        }

        if (!weekdayIndexes.includes(i) || isInvalidNumber) {
            weekdaysData.push(null);
        } else {
            weekdaysData.push(item);
        }

        if (!weekendIndexes.includes(i) || isInvalidNumber) {
            weekendsData.push(null);
        } else {
            weekendsData.push(item);
        }
    }

    return { allDaysData, weekdaysData, weekendsData };
};

export const formatValueBasedOnTypeInReport = (value: NullableNumber, itemType: ReportItemType, isShowFullRoom = false) => {
    let display = '';
    switch (itemType) {
        case 'room':
            display = formatValueHourInReport(value, isShowFullRoom, isShowFullRoom);
            break;
        case 'energy':
        case 'water':
            display = formatValueInReport(value);
            break;
        case 'temperature':
        case 'humidity':
            display = formatValueLessAccInReport(value);
            break;
        case 'waterLeakage':
        case 'co2':
        case 'pressure':
        case 'tvoc':
        case 'pm1':
        case 'pm2.5':
        case 'pm10':
            display = formatCountInReport(value);
            break;
    }

    return display;
}

export const formatValueAndUnitBasedOnTypeInReport = (
    itemType: ReportItemType,
    value: string | NullableNumber,
    unit: string,
    isShowFullRoom = false,
) => {
    let valueUnitDisplay = `${value}`;

    if (typeof value !== 'string' || value === null) {
        valueUnitDisplay = formatValueBasedOnTypeInReport(value, itemType, isShowFullRoom);
    }

    switch (itemType) {
        case 'room':
            break;
        case 'energy':
        case 'water':
            valueUnitDisplay = `${valueUnitDisplay} ${unit}`;
            break;
        case 'temperature':
        case 'humidity':
            valueUnitDisplay = `${valueUnitDisplay}${unit}`;
            break;
        case 'waterLeakage':
            break;
        case 'co2':
        case 'pressure':
        case 'tvoc':
        case 'pm1':
        case 'pm2.5':
        case 'pm10':
            valueUnitDisplay = `${valueUnitDisplay} ${unit}`;
            break;
    }

    return valueUnitDisplay;
};
